import { useCallback, useEffect, useRef } from 'react';
import { StyleSheet } from 'react-native';
import type { ViewToken } from 'react-native';
import {
  Typography,
  Flex,
  RemoteImage,
  IconLive,
  List,
  Colors,
} from '@banhala/ui-kit';
import type {
  LiveProfileBadgeListProps,
  LiveProfileBadgeItem,
  LiveProfileStatus,
} from './types';
import type { AnyObject } from '../../../../types';
import { isNil } from '../../../../utils';
import { useOpenDeeplink } from '../../../../hooks';
import { useLogAnalyticsEvent } from '../../../../hooks/useLogAnalyticsEvent';

const PADDING_HORIZONTAL = 12;
const GAP = 8;
const PROFILE_SIZE = 72;
const IMAGE_SIZE = 65;
const BADGE_HEIGHT = 16;
const ITEM_WIDTH = PROFILE_SIZE;

const LIVE_COLOR = '#EC0000';
const UNREAD_STORY_COLOR = '#006AFF';

// profile_status 별 프로필 테두리 스펙 (Android/iOS 네이티브와 동일).
const PROFILE_STATUS_BORDER: Record<
  LiveProfileStatus,
  { color: string; width: number }
> = {
  LIVE: { color: LIVE_COLOR, width: 2 },
  HAS_UNREAD_STORY: { color: UNREAD_STORY_COLOR, width: 2 },
  ALL_READ_STORY: { color: Colors.border_secondary, width: 2 },
  DEFAULT: { color: Colors.border_tertiary, width: 1 },
};

const getProfileStatusBorder = (status: LiveProfileStatus) =>
  PROFILE_STATUS_BORDER[status] ?? PROFILE_STATUS_BORDER.DEFAULT;

type BadgeEntity = { item: LiveProfileBadgeItem; logging: AnyObject };

interface LiveProfileBadgeIconProps {
  item: LiveProfileBadgeItem;
  logging: AnyObject;
}

const LiveProfileBadgeIcon = ({ item, logging }: LiveProfileBadgeIconProps) => {
  const { name, profile_image_url, profile_status, deeplink } = item;
  const isLive = profile_status === 'LIVE';
  const border = getProfileStatusBorder(profile_status);
  const { openDeeplink } = useOpenDeeplink();
  const logAnalyticsEvent = useLogAnalyticsEvent();

  const handlePress = useCallback(() => {
    if (deeplink) {
      logAnalyticsEvent({
        name: 'CLICK_LIVE_PROFILE_BADGE',
        analytics: logging.analytics,
        ably_only_log: logging.ably_only_log,
      });
      openDeeplink(deeplink);
    }
  }, [openDeeplink, deeplink, logging, logAnalyticsEvent]);

  return (
    <Flex width={ITEM_WIDTH} alignItems="center" onPress={handlePress}>
      <Flex
        width={PROFILE_SIZE}
        height={PROFILE_SIZE}
        borderRadius={PROFILE_SIZE / 2}
        borderWidth={border.width}
        borderColorValue={border.color}
        alignItems="center"
        justifyContent="center"
      >
        <Flex
          width={IMAGE_SIZE}
          height={IMAGE_SIZE}
          borderRadius={IMAGE_SIZE / 2}
          overflow="hidden"
        >
          <RemoteImage
            uri={profile_image_url}
            width={IMAGE_SIZE}
            height={IMAGE_SIZE}
          />
        </Flex>
      </Flex>

      {isLive && (
        <Flex
          margin={{ top: -8 }}
          height={BADGE_HEIGHT}
          borderRadius={BADGE_HEIGHT / 2}
          backgroundColorValue={LIVE_COLOR}
          alignItems="center"
          justifyContent="center"
          padding={{ horizontal: 10 }}
        >
          <IconLive width={17} height={8} fill={Colors.content_always_white} />
        </Flex>
      )}

      <Flex width="100%" margin={{ top: 8 }}>
        <Typography
          type="body4"
          color="content_primary"
          numberOfLines={1}
          textAlign="center"
        >
          {name}
        </Typography>
      </Flex>
    </Flex>
  );
};

const VIEWABILITY_CONFIG = { itemVisiblePercentThreshold: 1 };

const keyExtractor = (item: BadgeEntity) => item.item.sno.toString();

const renderItem = ({ item }: { item: BadgeEntity }) => (
  <LiveProfileBadgeIcon item={item.item} logging={item.logging} />
);

export const LiveProfileBadgeList = ({
  entity,
  isVisible,
  isAwaitingDataChange,
}: LiveProfileBadgeListProps) => {
  // GoodsCarousel.useLogDisplayGoods 패턴 — inner FlatList 가 부모(AtomicRenderer)
  // viewport 와 무관하게 viewable 을 보고하므로, emit 시점에 부모 가시성으로 가드 +
  // isVisible false→true 전이 시 캐시 기반 재emit 으로 노출 결손 방지.
  const loggedSnosRef = useRef<Set<number>>(new Set());
  const lastViewableItemsRef = useRef<ViewToken[]>([]);
  const isVisibleRef = useRef(isVisible);
  isVisibleRef.current = isVisible;
  const isAwaitingDataChangeRef = useRef(isAwaitingDataChange);
  isAwaitingDataChangeRef.current = isAwaitingDataChange;

  const logAnalyticsEvent = useLogAnalyticsEvent();

  const emitForViewable = useCallback(
    (viewableItems: ViewToken[]) => {
      if (isVisibleRef.current === false) return;
      if (isAwaitingDataChangeRef.current === true) return;
      viewableItems.forEach(({ isViewable, item }) => {
        if (!isViewable) return;
        const badgeData = item as BadgeEntity;
        const sno = badgeData.item.sno;
        if (loggedSnosRef.current.has(sno)) return;
        loggedSnosRef.current.add(sno);
        logAnalyticsEvent({
          channel: ['ablylog', 'firebase'],
          name: 'DISPLAY_LIVE_PROFILE_BADGE',
          analytics: badgeData.logging.analytics,
          ably_only_log: badgeData.logging.ably_only_log,
        });
      });
    },
    [logAnalyticsEvent],
  );

  useEffect(() => {
    if (isVisible && !isAwaitingDataChange) {
      emitForViewable(lastViewableItemsRef.current);
    }
  }, [isVisible, isAwaitingDataChange, emitForViewable]);

  const onViewableItemsChanged = useCallback(
    ({ viewableItems }: { viewableItems: ViewToken[] }) => {
      lastViewableItemsRef.current = viewableItems;
      emitForViewable(viewableItems);
    },
    [emitForViewable],
  );

  if (isNil(entity) || entity.length === 0) {
    return null;
  }

  return (
    <List
      horizontal
      data={entity}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.scrollContent}
      onViewableItemsChanged={onViewableItemsChanged}
      viewabilityConfig={VIEWABILITY_CONFIG}
    />
  );
};

const styles = StyleSheet.create({
  scrollContent: {
    paddingHorizontal: PADDING_HORIZONTAL,
    gap: GAP,
    paddingBottom: 12,
  },
});
